import { useEffect, useId, useState } from 'react';
import { ChevronRight } from 'lucide-react';
import { cn } from '@/lib/cn';

/**
 * One step, and only one, is the page.
 *
 * The packaging screen rendered every panel whose state matched - at
 * "building the structure" that was five at once - so nothing looked more
 * important than anything else and the whole thing read as a dump. A person
 * doing this job has exactly one decision in front of them; that decision
 * gets the page, and its supporting evidence sits under it rather than
 * beside five other things.
 */

export function StepCard({
  step,
  of,
  title,
  instruction,
  action,
  children,
  state = 'now',
  summary,
}: {
  step: number;
  of: number;
  title: string;
  instruction: string;
  /** The control that advances the run. */
  action?: React.ReactNode;
  /** Evidence for this decision. Shown, because a decision without it is a guess. */
  children?: React.ReactNode;
  /**
   * Whether this is the step to do now.
   *
   * Every card drew itself gold, so a finished step and the live one looked
   * identical - and in STRUCTURING the finished one sits higher on the page.
   * An operator who had just registered 34,247 records saw a gold card reading
   * "Already registered" and nothing telling them the next move was further
   * down. Gold is a claim about where you are, so only one card may make it.
   */
  state?: 'now' | 'done' | 'waiting';
  /** One line for a collapsed card: what it achieved, or what unlocks it. */
  summary?: string;
}) {
  const now = state === 'now';
  /**
   * A step that is not the one to do now collapses to its header.
   *
   * Every card drew its full instruction, its evidence and its control
   * whatever state it was in, so a run midway through showed eight open cards
   * and an operator had to find the live one among them. And because each card
   * was mounted only in its own run states, advancing made cards vanish and
   * others appear in the same frame - the page rearranging itself around the
   * one thing you were reading.
   *
   * Collapsed to a header, a finished step stays on the page as a record and a
   * waiting one as a promise, neither competing with the live card. It is also
   * what makes keeping them mounted safe: a header needs a title and a
   * sentence, never the payload the body would have read.
   */
  return (
    <section
      className={cn(
        'overflow-hidden rounded-md border bg-panel',
        now ? 'border-acc' : state === 'done' ? 'border-ok/40' : 'border-line',
      )}
    >
      <div
        className={cn(
          'flex flex-wrap items-baseline gap-x-3 gap-y-1 border-b border-line px-4 py-3',
          now ? 'bg-acc-soft' : 'bg-panel-2',
        )}
      >
        <span className={cn('mono text-caption', now ? 'text-acc-2' : state === 'done' ? 'text-ok' : 'text-ink-4')}>
          {state === 'done' ? '✓ ' : ''}Step {step} of {of}
        </span>
        <h2 className="text-h2 text-ink">{title}</h2>
        <p className="w-full text-small text-ink-2">{now ? instruction : (summary ?? '')}</p>
      </div>
      {now && children}
      {now && action && (
        <div className="flex flex-wrap items-center gap-3 border-t border-line px-4 py-3">{action}</div>
      )}
    </section>
  );
}

/** Everything that is not the current decision, one click away. */
export function Details({
  title,
  children,
  startOpen = false,
}: {
  title: string;
  children: React.ReactNode;
  /**
   * Open on arrival.
   *
   * A disclosure is right for something worth consulting and wrong for the one
   * control that clears a block: the page said a scope could not be read and
   * folded the registration that fixes it behind a row you have to know to
   * click.
   */
  startOpen?: boolean;
}) {
  const [open, setOpen] = useState(startOpen);
  /**
   * The fact that opens this usually arrives after the first render.
   *
   * `useState(startOpen)` reads its argument once, so a disclosure told to open
   * by a query that had not answered yet stayed shut for good - which is
   * exactly the case here, since whether a scope has a read grant is a read.
   * It opens when the answer lands, and does not reopen if the operator then
   * closes it.
   */
  useEffect(() => {
    if (startOpen) setOpen(true);
  }, [startOpen]);
  // A stable id so the control can name what it opens. Without it a screen
  // reader was told a button was expanded and given no way to reach what had
  // expanded, and the rotating chevron carrying the state is aria-hidden.
  const panelId = useId();
  return (
    <div className="overflow-hidden rounded-md border border-line bg-panel">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        aria-controls={panelId}
        className="flex w-full items-center gap-2 px-4 py-2.5 text-left text-small text-ink-3 transition-colors duration-fast hover:bg-hov hover:text-ink"
      >
        <ChevronRight className={cn('h-3.5 w-3.5 transition-transform', open && 'rotate-90')} aria-hidden="true" />
        {title}
      </button>
      {open && (
        <div id={panelId} className="border-t border-line">
          {children}
        </div>
      )}
    </div>
  );
}
